import StringInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/StringInputBuilder';
import BooleanInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/BooleanInputBuilder';

export function ToolParsingConfig({
  currentParams,
  setCurrentParams,
}: {
  currentParams: any;
  setCurrentParams: (value: any) => void;
}) {
  return (
    <>
      <BooleanInputBuilder
        name={'Enable Inline Tool Execution'}
        existingValue={!!currentParams.params.should_execute_tools}
        updateProperty={(value: boolean) => {
          setCurrentParams({
            ...currentParams,
            params: { ...currentParams.params, should_execute_tools: value },
          });
        }}
      />
      <div className="prose text-sm italic ml-8">
        This option allows the system to execute tools immediately based on the parsed content of
        the model's response. If enabled, the system will look for specific patterns in the response
        to determine which tool to execute.
      </div>

      {currentParams.params.should_execute_tools && (
        <>
          <StringInputBuilder
            name={'Tool ID Regex'}
            existingValue={currentParams.params.tool_parsing_params?.toolIdRegex}
            updateProperty={(value: string) => {
              setCurrentParams({
                ...currentParams,
                params: {
                  ...currentParams.params,
                  tool_parsing_params: {
                    ...currentParams.params?.tool_parsing_params,
                    toolIdRegex: value,
                  },
                },
              });
            }}
          />
          <div className="prose text-sm italic">
            This regular expression is used to identify the tool ID from the model's response. For
            example, if the response contains a tool ID like
            "[function_name]some_name[/function_name]", you can use a regex pattern to extract
            "some_name" from the response.
            <br />
            If you want to specify a tool without extracting it from the response, surround the tool
            ID with double quotes. For example, "my_tool", and the system will use my_tool directly
            instead of using regex to parse the model response.
          </div>

          <StringInputBuilder
            name={'Tool Input Regex'}
            existingValue={currentParams.params.tool_parsing_params?.toolInputRegex}
            updateProperty={(value: string) => {
              setCurrentParams({
                ...currentParams,
                params: {
                  ...currentParams.params,
                  tool_parsing_params: {
                    ...currentParams.params?.tool_parsing_params,
                    toolInputRegex: value,
                  },
                },
              });
            }}
          />
          <div className="prose text-sm italic">
            This regular expression is used to extract the input parameters for the tool from the
            model's response. For example, if the response contains input data like
            "[function_input]some_function_input[/function_input]", you can use a regex pattern to
            extract "some_function_input".
          </div>

          <StringInputBuilder
            name={'Reconstruction Template'}
            existingValue={currentParams.params.tool_parsing_params?.reconstructionTemplate}
            updateProperty={(value: string) => {
              setCurrentParams({
                ...currentParams,
                params: {
                  ...currentParams.params,
                  tool_parsing_params: {
                    ...currentParams.params?.tool_parsing_params,
                    reconstructionTemplate: value,
                  },
                },
              });
            }}
          />
          <div className="prose text-sm italic">
            Reconstruction Template: This template is used to reconstruct the message after the tool
            has been executed. It allows you to format the final message that will be passed to the
            next step. For example, you can use placeholders like {`{originalContent}`} and
            {`{toolOutput}`} to include the original message and the tool's output in the final
            message.
          </div>

          {(!currentParams.params.tool_parsing_params?.reconstructionTemplate?.includes(
            '{originalContent}',
          ) ||
            !currentParams.params.tool_parsing_params?.reconstructionTemplate?.includes(
              '{toolOutput}',
            )) && (
            <p style={{ color: 'red' }}>
              Warning: The template should include both {`{originalContent}`} and {`{toolOutput}`}{' '}
              placeholders.
            </p>
          )}

          <StringInputBuilder
            name={'Tool Output Regex'}
            existingValue={currentParams.params.tool_parsing_params?.toolOutputRegex}
            updateProperty={(value: string) => {
              setCurrentParams({
                ...currentParams,
                params: {
                  ...currentParams.params,
                  tool_parsing_params: {
                    ...currentParams.params?.tool_parsing_params,
                    toolOutputRegex: value,
                  },
                },
              });
            }}
          />
          <div className="prose text-sm italic">
            This regex is used to recover the tool output from a message that has been reconstructed
            using the Reconstruction Template. It allows you to extract the tool's output from the
            final message.
          </div>

          {!new RegExp(currentParams.params.tool_parsing_params?.toolOutputRegex).test(
            currentParams.params.tool_parsing_params?.reconstructionTemplate || '',
          ) && (
            <p style={{ color: 'red' }}>
              Warning: The Tool Output Regex does not accurately correspond to the Reconstruction
              Template. Please ensure the regex matches the format used in the template.
            </p>
          )}
        </>
      )}
    </>
  );
}
